// @flow
import * as React from 'react';
import Modal from 'react-modal';
import { capitalize, debounce } from 'lodash';
import idx from 'idx';

import { setAutoMessageStatus, duplicateAutoMessage } from 'admin/actions/AutoMessageActions';
import AdminAutoMessageDetailPreviousSendsTable from './AdminAutoMessageDetailPreviousSendsTable.jsx';
import AdminAutoMessageDetailUnsentFilesTable from './AdminAutoMessageDetailUnsentFilesTable.jsx';
import AdminAutoMessageDetailViewActions from './AdminAutoMessageDetailViewActions.jsx';
import AdminNewAutoMessageModal from 'admin/components/AdminNewAutoMessage';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Heading from 'components/Heading.jsx';
import Glyph from 'components/Glyph.jsx';
import Link from 'components/Link.jsx';
import MediaObject from 'components/MediaObject.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import TextBadge from 'components/TextBadge';
import VisibilityWatch from 'components/VisibilityWatch.jsx';

import type { GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages } from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageByUuid.js';

type Props = {
  organizationUUID: string,
  autoMessage: GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages,
  loading: ?boolean,
  onRedirect: (messageUuid: string) => void,
  urlSlug: string,
  loadMoreSends: (cursor: string) => void,
};

type State = {
  scrolledToBottom: boolean,
  isNewAutoMessageDialogOpened: boolean,
};

class AdminAutoMessageDetailView extends React.Component<Props, State> {
  state = {
    scrolledToBottom: false,
    isNewAutoMessageDialogOpened: false,
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { autoMessage } = this.props;
    if (!autoMessage) {
      return;
    }

    const autoMessagePreviousSends = idx(autoMessage, _ => _.previousSends.edges);

    const lastCursor =
      autoMessagePreviousSends && autoMessagePreviousSends.length > 0
        ? autoMessagePreviousSends.slice(-1)[0].cursor
        : null;

    if (!prevState.scrolledToBottom && this.state.scrolledToBottom && lastCursor) {
      this.props.loadMoreSends(lastCursor);
    }
  }

  toggleNewAutoMessageDialog = () => {
    this.setState({ isNewAutoMessageDialogOpened: !this.state.isNewAutoMessageDialogOpened });
  };

  renderNotice = (
    status: 'draft' | 'inactive',
    csvName: string,
    unsentFileUploads?: $ReadOnlyArray<*>
  ) => {
    const statusToHeading = {
      draft: 'This auto message is a draft',
      inactive: 'This auto message is inactive',
    };

    const copyWithoutReceivedFiles = (
      <Text size="small" lineHeight="large">
        Once Remind receives {csvName || 'a CSV file'}, messages that were not sent will appear
        here. You’ll be able to see who the messages would have been sent to, if this auto message
        had been active.
      </Text>
    );

    return (
      <Row>
        <AdminNoticeBanner
          alwaysShowDetails
          actionElement={undefined}
          color="brand"
          details={
            unsentFileUploads && unsentFileUploads.length ? (
              <AdminAutoMessageDetailUnsentFilesTable unsentFileUploads={unsentFileUploads} />
            ) : (
              copyWithoutReceivedFiles
            )
          }>
          <Heading level="3" flush>
            {statusToHeading[status]}
          </Heading>
        </AdminNoticeBanner>
      </Row>
    );
  };

  setScrolledToBottom = debounce(
    (scrolledToBottom: boolean) => {
      this.setState({ scrolledToBottom });
    },
    200,
    { leading: true, trailing: true }
  );

  renderVisibilityWatcher() {
    return (
      <VisibilityWatch
        whileVisible={() => this.setScrolledToBottom(true)}
        whileInvisible={() => this.setScrolledToBottom(false)}
        interval={500}>
        <div />
      </VisibilityWatch>
    );
  }

  render() {
    const { loading, autoMessage, urlSlug, organizationUUID, onRedirect } = this.props;
    if (!loading && !autoMessage) {
      return (
        <ErrorReporter error="AdminAutoMessageDetailView tried to render without an autoMessage" />
      );
    }

    if (!autoMessage) {
      return <CenteredLoadingIndicator />;
    }

    const {
      unsentFileUploads,
      permissions,
      status,
      messageName,
      isActivatable,
      previousSends,
      csvName,
      uuid,
    } = autoMessage;

    const statusToColor = {
      active: 'shamrock',
      inactive: 'sunrise',
      draft: 'eclipse',
    };

    const messageIsActive = status === 'active';

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isNewAutoMessageDialogOpened}>
          <AdminNewAutoMessageModal
            onBack={this.toggleNewAutoMessageDialog}
            organizationUUID={organizationUUID}
            messageUuid={uuid}
          />
        </Modal>
        <Row size="small">
          <Heading level="2">
            <MediaObject figure="right" center>
              <MediaObject center gutter="small">
                <MediaObject center gutter="small">
                  <Link to={urlSlug}>Auto messages</Link>
                  <Glyph size="medium" name="chevron-right" />
                </MediaObject>
                <MediaObject center gutter="small" figure="right" inline>
                  <Text truncate>{messageName}</Text>
                  <TextBadge textSize="xxxxsmall" textColor="white" color={statusToColor[status]}>
                    {capitalize(status)}
                  </TextBadge>
                </MediaObject>
              </MediaObject>
              <AdminAutoMessageDetailViewActions
                messageIsActive={messageIsActive}
                canEdit={permissions ? permissions.canEdit : false}
                isActivatable={isActivatable}
                onEdit={this.toggleNewAutoMessageDialog}
                onDuplicate={() => duplicateAutoMessage(autoMessage, organizationUUID, onRedirect)}
                onChangeStatus={() =>
                  setAutoMessageStatus(
                    autoMessage.uuid,
                    messageIsActive ? 'inactive' : 'active',
                    organizationUUID,
                    autoMessage.messageName
                  )
                }
              />
            </MediaObject>
          </Heading>
        </Row>
        {permissions &&
          !permissions.canEdit && (
            <Row size="small">
              <Text color="eclipse" size="xsmall">
                Only the district admin can edit this auto message.
              </Text>
            </Row>
          )}
        {status !== 'active' && this.renderNotice(status, csvName, unsentFileUploads)}
        <AdminAutoMessageDetailPreviousSendsTable previousSends={previousSends} csvName={csvName} />
        {this.renderVisibilityWatcher()}
      </React.Fragment>
    );
  }
}

export default AdminAutoMessageDetailView;
